import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LucidePencil } from "lucide-react";
import { Modal, ModalBody, ModalFooter } from "@/components/modals/Modal";

const GRADE_OPTIONS = ["1", "2", "3", "4", "5", "6", "7", "8"];

function getSchoolYears() {
  const y = new Date().getFullYear();
  return [`${y - 1}–${y}`, `${y}–${y + 1}`, `${y + 1}–${y + 2}`];
}

const nativeSelect =
  "h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm " +
  "ring-offset-background transition-colors focus:outline-none focus:ring-2 " +
  "focus:ring-ring focus:ring-offset-2 text-foreground";

/**
 * Modal for editing an existing Report Folder's name, grade, and school year.
 *
 * @param {object} props
 * @param {object|null} props.reportFolder - The Report Folder being edited, or null if closed
 * @param {() => void} props.onClose
 * @param {(reportFolder: object, editForm: object) => Promise<void>} props.onSubmit
 * @param {boolean} props.updating
 */
export function EditReportFolderModal({ reportFolder, onClose, onSubmit, updating }) {
  const [editForm, setEditForm] = useState({
    name: "",
    grade: "",
    schoolYear: "",
  });

  useEffect(() => {
    if (reportFolder) {
      setEditForm({
        name: reportFolder.name || "",
        grade: reportFolder.grade || "",
        schoolYear: reportFolder.schoolYear || "",
      });
    }
  }, [reportFolder]);

  const schoolYears = getSchoolYears();

  async function handleSubmit(e) {
    e.preventDefault();
    await onSubmit(reportFolder, editForm);
  }

  return (
    <Modal
      open={!!reportFolder}
      onClose={onClose}
      title="Edit Report Folder"
      subtitle="Update the Report Folder name, grade, or school year."
      icon={<LucidePencil size={16} className="text-primary" />}
    >
      <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
        <ModalBody className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">
              Report Folder Name{" "}
              <span className="text-muted-foreground font-normal text-xs">
                (optional)
              </span>
            </Label>
            <Input
              value={editForm.name}
              onChange={(e) =>
                setEditForm((p) => ({ ...p, name: e.target.value }))
              }
              className="h-10"
            />
          </div>
          <div className="flex gap-3">
            <div className="space-y-1.5 shrink-0" style={{ width: 96 }}>
              <Label className="text-sm font-medium">Grade</Label>
              <select
                value={editForm.grade}
                onChange={(e) =>
                  setEditForm((p) => ({ ...p, grade: e.target.value }))
                }
                className={nativeSelect}
              >
                <option value="">-</option>
                {GRADE_OPTIONS.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5 flex-1">
              <Label className="text-sm font-medium">School Year</Label>
              <select
                value={editForm.schoolYear}
                onChange={(e) =>
                  setEditForm((p) => ({ ...p, schoolYear: e.target.value }))
                }
                className={nativeSelect}
              >
                <option value="">Select year</option>
                {schoolYears.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button type="submit" disabled={updating} className="flex-1">
            {updating ? "Saving..." : "Save Changes"}
          </Button>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
